import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Download,
  Edit3,
  Eye,
  Filter,
  History,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Send,
  TicketCheck,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlowpiDialogSurface } from "../components/ui/flowpi-dialog";
import CannibalHistoryDrawer from "../components/service/CannibalHistoryDrawer";
import CannibalTransferDialog, {
  CANNIBAL_TRANSFER_DEFAULT_COMPANY,
  buildCannibalTransferPayload,
} from "../components/service/CannibalTransferDialog";
import { ServiceBranchSearchRow } from "../components/service/ServiceListControls";
import SearchableSelect from "../components/SearchableSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import {
  DataValue,
  PassiveBadge,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListErrorNotice,
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnSelectFilter,
  ColumnTextFilter,
  DateRangeSelector,
  GlobalSearch,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import {
  checkServiceSerialNumber,
  createServiceTicket,
  createCannibalDevice,
  downloadCannibalExport,
  exportCannibalDevices,
  getCannibalDevice,
  getCannibalTransferPrefill,
  listActiveServiceDeviceModels,
  listCannibalDevices,
  listItemMaster,
  updateCannibalDevice,
} from "../lib/serviceApi";
import {
  SERVICE_BRANCHES,
  SERVICE_BRANCH_LABELS,
} from "../lib/serviceConstants";
import { canMutatePreExistingRemovedParts } from "../lib/servicePermissions";

const EMPTY_FORM = {
  serial_number: "",
  model_selection: "",
  model: "",
  stock_reference_status: "",
  branch: "jakarta",
  notes: "",
  unknown_stock_reference_confirmed: false,
  sn_result: "",
};

const formatDate = formatPresentationTimestamp;
const formatContextDate = (value) => (value ? new Date(value).toLocaleString() : "-");
const unwrap = (response) => response?.data || response || null;
const cleanText = (value) => (typeof value === "string" ? value.trim() : "");
const CANNIBAL_COLUMN_FILTER_KEYS = Object.freeze([
  "cannibal_id",
  "serial_number",
  "model",
  "stock_reference_status",
  "removed_parts",
  "created_date",
  "updated_date",
]);
const EMPTY_CANNIBAL_COLUMN_FILTERS = Object.freeze(
  Object.fromEntries(CANNIBAL_COLUMN_FILTER_KEYS.map((key) => [key, ""])),
);
const EMPTY_CANNIBAL_CREATED_DATE_RANGE = Object.freeze({
  from: "",
  to: "",
});
const CANNIBAL_STOCK_REFERENCE_OPTIONS = Object.freeze([
  { value: "known", label: "Known" },
  { value: "unknown", label: "Unknown" },
]);
const CANNIBAL_TABLE_GEOMETRY = Object.freeze([
  { key: "cannibal_id", kind: "fixed", width: 230 },
  { key: "serial_number", kind: "fixed", width: 190 },
  { key: "model", kind: "fill", minWidth: 280 },
  { key: "stock_reference_status", kind: "fixed", width: 150 },
  { key: "removed_parts", kind: "fixed", width: 110 },
  { key: "created_at", kind: "fixed", width: 170 },
  { key: "updated_at", kind: "fixed", width: 170 },
  { key: "actions", kind: "utility", width: 150 },
]);

const normalizePreExistingRows = (rows, itemRows) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const itemId = cleanText(row?.item_id);
    const item = (Array.isArray(itemRows) ? itemRows : []).find(
      (candidate) => candidate.id === itemId,
    );
    return {
      model: cleanText(row?.model) || cleanText(item?.model),
      item_id: itemId,
      recipient_serial_number: cleanText(row?.recipient_serial_number),
    };
  });

const buildPreExistingPatchRows = (rows) =>
  rows
    .map((row) => ({
      model: cleanText(row?.model),
      item_id: cleanText(row?.item_id),
      recipient_serial_number: cleanText(row?.recipient_serial_number),
    }))
    .filter((row) => row.model || row.item_id || row.recipient_serial_number)
    .map(({ model, item_id, recipient_serial_number }) => ({
      model,
      item_id,
      ...(recipient_serial_number ? { recipient_serial_number } : {}),
    }));

function StatusBadge({ value }) {
  const active = value === "active";
  return (
    <PassiveBadge tone={active ? "success" : "neutral"} data-testid={`cannibal-status-${value}`}>
      {active ? "Active" : "Transferred"}
    </PassiveBadge>
  );
}

function RemovedParts({ donor }) {
  const records = donor?.removed_parts || [];
  return (
    <div
      className="overflow-x-auto border-x border-zinc-200 dark:border-zinc-700"
      data-testid="cannibal-removed-parts-frame"
    >
      <table
        className="wms-table min-w-[850px]"
        data-testid="cannibal-removed-parts"
      >
        <thead>
          <tr>
            <th>Origin</th>
            <th>Part</th>
            <th>Status</th>
            <th>Recipient Ticket</th>
            <th>Recipient SN</th>
            <th>Removed</th>
            <th>Cancellation / Reversal</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-8 text-center text-zinc-500">
                No removed-part records.
              </td>
            </tr>
          ) : (
            records.map((record) => {
              const usage = record.origin === "cannibal_usage";
              const status = usage ? record.usage_status : record.status;
              return (
                <tr
                  key={`${record.origin}-${record.id}`}
                  data-origin={record.origin}
                  data-status={status}
                >
                  <td>
                    <PassiveBadge tone="info">
                      {usage ? "Cannibal Usage" : "Pre-existing"}
                    </PassiveBadge>
                  </td>
                  <td>
                    <div className="text-xs">{record.part_name}</div>
                    <div className="text-[10px] text-zinc-500">
                      {record.part_number}
                    </div>
                  </td>
                  <td className="text-xs capitalize">{status || "-"}</td>
                  <td className="text-xs">
                    {usage ? (
                      record.service_ticket_number || "-"
                    ) : (
                      <span className="text-zinc-400">Not applicable</span>
                    )}
                  </td>
                  <td className="text-xs">
                    {record.recipient_serial_number || "-"}
                  </td>
                  <td className="text-xs">
                    {formatContextDate(record.created_at)}
                    <br />
                    {record.created_by_email || ""}
                  </td>
                  <td className="text-xs">
                    {formatContextDate(record.reversed_at)}
                    <br />
                    {record.reversal_reason || ""}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export function DonorOverlay({ mode, donorId, role, onClose, onChanged }) {
  const navigate = useNavigate();
  const [donor, setDonor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [models, setModels] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(donorId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preExistingParts, setPreExistingParts] = useState([]);
  const [pendingPartDelete, setPendingPartDelete] = useState(null);
  const snRequest = useRef(0);
  const adminParts = canMutatePreExistingRemovedParts(role);
  const editable = mode === "edit" && donor?.status === "active";

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listActiveServiceDeviceModels(),
      mode === "edit" ? listItemMaster() : Promise.resolve([]),
      donorId ? getCannibalDevice(donorId) : Promise.resolve(null),
    ])
      .then(([modelRows, itemRows, response]) => {
        if (cancelled) return;
        const current = unwrap(response);
        setModels(modelRows || []);
        setItems(itemRows || []);
        setDonor(current);
        if (current) {
          setForm({
            ...EMPTY_FORM,
            ...current,
            unknown_stock_reference_confirmed:
              current.stock_reference_status === "unknown",
            sn_result: current.stock_reference_status,
          });
          setPreExistingParts(
            normalizePreExistingRows(current.pre_existing_removed_parts, itemRows),
          );
        }
      })
      .catch((reason) => !cancelled && setError(formatApiError(reason)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [adminParts, donorId, mode]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };
  const generate = async () => {
    const serial = form.serial_number.trim();
    if (!serial) return setError("Serial Number is required.");
    const requestId = ++snRequest.current;
    setLoading(true);
    try {
      const response = await checkServiceSerialNumber({
        serial_number: serial,
      });
      if (requestId !== snRequest.current) return;
      const result = unwrap(response) || {};
      setForm((current) => ({
        ...current,
        sn_result: result.result || "unknown",
        stock_reference_status: result.stock_reference_status || "unknown",
        model_selection:
          result.result === "known" ? "other" : current.model_selection,
        model: result.result === "known" ? result.model || "" : current.model,
        unknown_stock_reference_confirmed: false,
      }));
    } catch (reason) {
      setError(formatApiError(reason));
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      let response;
      if (mode === "create") {
        if (
          !form.serial_number.trim() ||
          !form.model_selection ||
          !form.branch ||
          !form.sn_result
        )
          throw new Error(
            "Generate SN Check and complete all required fields.",
          );
        response = await createCannibalDevice({
          serial_number: form.serial_number.trim(),
          model_selection: form.model_selection,
          unknown_stock_reference_confirmed:
            form.stock_reference_status === "unknown" &&
            form.unknown_stock_reference_confirmed,
          branch: form.branch,
          notes: form.notes.trim(),
        });
      } else {
        const submittedParts = buildPreExistingPatchRows(preExistingParts);
        if (adminParts && editable) {
          if (submittedParts.some((part) => !part.model || !part.item_id))
            throw new Error("Select Model and Item for every Pre-existing Removed Part.");
          const itemIds = submittedParts.map((part) => part.item_id);
          if (new Set(itemIds).size !== itemIds.length)
            throw new Error("Each Pre-existing Removed Part Item may be selected only once.");
        }
        response = await updateCannibalDevice(donor.id, {
          expected_updated_at: donor.updated_at,
          branch: form.branch,
          notes: form.notes.trim(),
          ...(adminParts && editable
            ? { pre_existing_removed_parts: submittedParts }
            : {}),
        });
      }
      const updated = unwrap(response);
      toast.success(
        mode === "create" ? "Cannibal donor created" : "Cannibal donor updated",
      );
      onChanged?.(updated);
      onClose();
    } catch (reason) {
      setError(formatApiError(reason));
    } finally {
      setSaving(false);
    }
  };

  const modelOptions = useMemo(
    () => models.filter((row) => row?.is_active).map((row) => row.name),
    [models],
  );
  const readOnly = mode === "view" || donor?.status === "transferred";
  const partModels = useMemo(
    () => [...new Set(items.flatMap((item) => [item.model, ...(item.compatible_models || [])]).filter(Boolean))].sort(),
    [items],
  );
  const partModelOptions = useMemo(
    () => partModels.map((model) => ({ value: model, label: model })),
    [partModels],
  );
  const updatePreExistingPart = (index, patch) => {
    setPreExistingParts((current) => current.map((part, partIndex) => partIndex === index ? { ...part, ...patch } : part));
    setError("");
  };
  const removePreExistingPart = (index) => {
    const part = preExistingParts[index];
    if (part?.item_id) {
      setPendingPartDelete(index);
      return;
    }
    setPreExistingParts((current) => current.filter((_, partIndex) => partIndex !== index));
  };
  const confirmPreExistingPartDelete = () => {
    if (pendingPartDelete === null) return;
    setPreExistingParts((current) => current.filter((_, partIndex) => partIndex !== pendingPartDelete));
    setPendingPartDelete(null);
  };
  return (
    <FlowpiDialogSurface open onClose={onClose} busy={saving} size="workflow" accessibleTitle={mode === "create" ? "Add Cannibal Device" : mode === "edit" ? "Edit Cannibal Device" : "Donor Detail"} testId={`cannibal-${mode}-dialog`}>
      <div className="flex max-h-[calc(100vh-48px)] w-[1040px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-950">
        <div className="flex items-start justify-between border-b border-zinc-200 px-6 py-5 dark:border-zinc-700">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
              Cannibal Device
            </div>
            <h2 className="mt-1 text-xl font-semibold">
              {mode === "create"
                ? "Add Cannibal Device"
                : mode === "edit"
                  ? "Edit Cannibal Device"
                  : "Donor Detail"}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          {loading && !donor && mode !== "create" ? (
            <p className="text-sm text-zinc-500">Loading donor...</p>
          ) : null}
          {error ? (
            <div
              className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              role="alert"
            >
              {error}
            </div>
          ) : null}
          {donor ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-500">
                Updated {formatDate(donor.updated_at)}
              </div>
              <StatusBadge value={donor.status} />
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-xs font-semibold">
              Serial Number
              <input
                value={form.serial_number}
                onChange={(event) =>
                  setField("serial_number", event.target.value)
                }
                readOnly={mode !== "create"}
                className="mt-2 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2.5 text-sm read-only:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:read-only:bg-zinc-900"
              />
            </label>
            {mode === "create" ? (
              <div className="flex items-end">
                <Button
                  onClick={generate}
                  loading={loading}
                  loadingLabel="Generating..."
                  data-testid="cannibal-generate-sn"
                >
                  Generate SN Check
                </Button>
              </div>
            ) : null}
            <label className="text-xs font-semibold">
              Model Selection
              <select
                value={form.model_selection}
                onChange={(event) => {
                  const value = event.target.value;
                  setField("model_selection", value);
                  setField("model", value);
                }}
                disabled={readOnly || form.sn_result === "known"}
                className="mt-2 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2.5 text-sm disabled:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="">Select model</option>
                {form.model_selection === "other" ? (
                  <option value="other">Other</option>
                ) : null}
                {modelOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold">
              Generated Model
              <input
                value={form.model || "Not generated"}
                readOnly
                className="mt-2 w-full rounded-sm border border-zinc-200 bg-zinc-100 px-3 py-2.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </label>
            <label className="text-xs font-semibold">
              Stock Reference Status
              <div className="mt-2 flex items-center gap-3 rounded-sm border border-zinc-200 bg-zinc-100 px-3 py-2.5 text-sm dark:border-zinc-700 dark:bg-zinc-900">
                <span className="capitalize">
                  {form.stock_reference_status || "Not generated"}
                </span>
                {mode === "create" &&
                form.stock_reference_status === "unknown" ? (
                  <label className="ml-auto flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={form.unknown_stock_reference_confirmed}
                      onChange={(event) =>
                        setField(
                          "unknown_stock_reference_confirmed",
                          event.target.checked,
                        )
                      }
                    />
                    Confirm unknown reference
                  </label>
                ) : null}
              </div>
            </label>
            <label className="text-xs font-semibold">
              Branch
              <select
                value={form.branch}
                onChange={(event) => setField("branch", event.target.value)}
                disabled={readOnly}
                className="mt-2 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2.5 text-sm disabled:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900"
              >
                {Object.values(SERVICE_BRANCHES).map((branch) => (
                  <option key={branch} value={branch}>
                    {SERVICE_BRANCH_LABELS[branch]}
                  </option>
                ))}
              </select>
            </label>
            {donor?.status === "transferred" &&
            cleanText(donor.transferred_service_ticket_id) &&
            cleanText(donor.transferred_service_ticket_number) ? (
              <div
                className="flex min-w-0 items-center gap-3 self-end rounded-sm border border-warning/40 bg-warning/10 px-3 py-2"
                data-testid="cannibal-transferred-service-ticket"
              >
                <TicketCheck
                  className="h-5 w-5 shrink-0 text-warning"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-warning">
                    Transferred Service Ticket
                  </div>
                  <div className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    {donor.transferred_service_ticket_number}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="info"
                  size="sm"
                  className="shrink-0"
                  onClick={() =>
                    navigate(
                      `/service/tickets/${encodeURIComponent(donor.transferred_service_ticket_id)}`,
                    )
                  }
                  aria-label={`View Service Ticket ${donor.transferred_service_ticket_number}`}
                >
                  View
                </Button>
              </div>
            ) : null}
            <label className="text-xs font-semibold md:col-span-2">
              Notes
              <textarea
                value={form.notes}
                onChange={(event) => setField("notes", event.target.value)}
                readOnly={readOnly}
                className="mt-2 min-h-24 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2.5 text-sm read-only:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900"
              />
            </label>
          </div>
          {donor ? (
            <section>
              <h3 className="mb-3 text-sm font-semibold">
                Removed-part traceability
              </h3>
              <RemovedParts donor={donor} />
            </section>
          ) : null}
          {mode === "edit" && donor ? (
            <section className="rounded-sm border border-zinc-200 p-4 dark:border-zinc-700">
              <h3 className="text-sm font-semibold">Pre-existing Removed Parts</h3>
              <p className="mt-1 text-xs text-zinc-500">Current parts already missing from this donor. They never create Usage or a Service Ticket.</p>
              <div className="mt-4 space-y-3">
                {preExistingParts.length === 0 ? <p className="text-xs text-zinc-500">No Pre-existing Removed Parts.</p> : null}
                {preExistingParts.map((part, index) => {
                  const selectedElsewhere = new Set(preExistingParts.filter((_, partIndex) => partIndex !== index).map((row) => row.item_id));
                  const itemOptions = items.filter((item) => (!part.model || item.model === part.model || (item.compatible_models || []).includes(part.model)) && (!selectedElsewhere.has(item.id) || item.id === part.item_id)).map((item) => ({ value: item.id, label: `${item.part_number} · ${item.part_name}` }));
                  return <div key={`${part.item_id || "new"}-${index}`} className="grid min-w-0 items-start gap-2 md:grid-cols-[minmax(150px,0.8fr)_minmax(260px,1.6fr)_minmax(190px,1fr)_40px]">
                    <div className="min-w-0"><SearchableSelect value={part.model} onChange={(value) => updatePreExistingPart(index, { model: value, item_id: "" })} options={part.model && !partModels.includes(part.model) ? [{ value: part.model, label: part.model }, ...partModelOptions] : partModelOptions} placeholder="Select Model" disabled={!adminParts || !editable} testid={`pre-existing-model-${index}`} allowClear portal /></div>
                    <div className="min-w-0"><SearchableSelect value={part.item_id} onChange={(value) => updatePreExistingPart(index, { item_id: value })} options={itemOptions} placeholder={part.model ? "Select Item" : "Select Model first"} disabled={!adminParts || !editable || !part.model} testid={`pre-existing-item-${index}`} allowClear portal /></div>
                    <input value={part.recipient_serial_number} onChange={(event) => updatePreExistingPart(index, { recipient_serial_number: event.target.value })} readOnly={!adminParts || !editable} placeholder="Recipient Serial Number (optional)" className="h-[42px] min-w-0 rounded-lg border border-zinc-300 bg-white px-3 text-sm read-only:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900" />
                    {adminParts && editable ? <Button type="button" variant="transfer-remove" size="row-icon" onClick={() => removePreExistingPart(index)} title="Remove item" aria-label={`Remove Pre-existing part ${index + 1}`}><Trash2 /></Button> : <span className="h-8 w-8" />}
                  </div>;
                })}
              </div>
              {adminParts && editable ? <Button type="button" variant="transfer-add" size="sm" className="mt-4" onClick={() => setPreExistingParts((current) => [...current, { model: "", item_id: "", recipient_serial_number: "" }])}><Plus /> Add Item</Button> : <p className="mt-3 text-xs text-zinc-500">{donor.status === "transferred" ? "Transferred donor parts are read-only." : "Pre-existing Removed Parts are read-only for Staff."}</p>}
            </section>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-zinc-200 bg-zinc-50 px-6 py-4 dark:border-zinc-700 dark:bg-zinc-900">
          <Button variant="cancel" onClick={onClose}>
            Close
          </Button>
          {mode === "create" || editable ? (
            <Button variant="success" onClick={save} loading={saving} loadingLabel="Saving...">
              {mode === "create" ? "Add Device" : "Confirm Changes"}
            </Button>
          ) : null}
        </div>
      </div>
      <AlertDialog open={pendingPartDelete !== null} onOpenChange={(open) => !open && setPendingPartDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Pre-existing Removed Part?</AlertDialogTitle>
            <AlertDialogDescription>
              This part will be removed from the donor's current missing-parts list when changes are saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingPartDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPreExistingPartDelete} variant="destructive">
              Remove Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FlowpiDialogSurface>
  );
}

export default function CannibalDevices() {
  const { user } = useAuth();
  const [donors, setDonors] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    branch: SERVICE_BRANCHES.JAKARTA,
    status: "active",
    ...EMPTY_CANNIBAL_COLUMN_FILTERS,
    created_date_from: "",
    created_date_to: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [columnFilterDrafts, setColumnFilterDrafts] = useState({
    ...EMPTY_CANNIBAL_COLUMN_FILTERS,
  });
  const [createdDateRange, setCreatedDateRange] = useState({
    ...EMPTY_CANNIBAL_CREATED_DATE_RANGE,
  });
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [overlay, setOverlay] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [transferPrefill, setTransferPrefill] = useState(null);
  const [transferCompanyName, setTransferCompanyName] = useState(CANNIBAL_TRANSFER_DEFAULT_COMPANY);
  const [transferLoadingId, setTransferLoadingId] = useState(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, page_size: pageSize };
    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim()) params[key] = value.trim();
    });
    listCannibalDevices(params)
      .then((response) => {
        if (!cancelled) {
          const nextPagination = response?.pagination || { page, total: 0, total_pages: 0 };
          const lastPage = Math.max(nextPagination.total_pages || 0, 1);
          if (page > lastPage) {
            setPage(lastPage);
            return;
          }
          setDonors(response?.data || []);
          setPagination(nextPagination);
          setError("");
        }
      })
      .catch((reason) => !cancelled && setError(formatApiError(reason)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters, page, pageSize, refresh]);
  const applyFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };
  const updateColumnFilterDraft = (key, value) => {
    setColumnFilterDrafts((current) => ({ ...current, [key]: value }));
  };
  const applyCreatedDateRange = (range) => {
    setFilters((current) => ({
      ...current,
      created_date_from: range.from || "",
      created_date_to: range.to || "",
    }));
    setPage(1);
  };
  const clearAllFilters = () => {
    setSearchInput("");
    setColumnFilterDrafts({ ...EMPTY_CANNIBAL_COLUMN_FILTERS });
    setCreatedDateRange({ ...EMPTY_CANNIBAL_CREATED_DATE_RANGE });
    setFilterResetKey((value) => value + 1);
    setFilters((current) => ({
      search: "",
      branch: current.branch,
      status: current.status,
      ...EMPTY_CANNIBAL_COLUMN_FILTERS,
      created_date_from: "",
      created_date_to: "",
    }));
    setPage(1);
  };
  const runExport = async () => {
    setExporting(true);
    try {
      downloadCannibalExport(await exportCannibalDevices());
    } catch (reason) {
      setError(formatApiError(reason));
    } finally {
      setExporting(false);
    }
  };
  const openTransfer = async (donorId) => {
    if (transferLoadingId || transferSubmitting) return;
    setTransferLoadingId(donorId);
    setTransferError("");
    try {
      const response = await getCannibalTransferPrefill(donorId);
      setTransferPrefill(response?.data || response);
      setTransferCompanyName(CANNIBAL_TRANSFER_DEFAULT_COMPANY);
    } catch (reason) {
      toast.error(formatApiError(reason));
    } finally {
      setTransferLoadingId(null);
    }
  };
  const closeTransfer = () => {
    if (transferSubmitting) return;
    setTransferPrefill(null);
    setTransferCompanyName(CANNIBAL_TRANSFER_DEFAULT_COMPANY);
    setTransferError("");
  };
  const submitTransfer = async () => {
    if (!transferPrefill || transferSubmitting) return;
    const companyName = transferCompanyName.trim();
    if (!companyName) {
      setTransferError("Company Name is required.");
      return;
    }
    setTransferSubmitting(true);
    setTransferError("");
    try {
      const response = await createServiceTicket(buildCannibalTransferPayload({
        prefill: transferPrefill,
        companyName,
      }));
      const ticketNumber = response?.data?.ticket_number || response?.ticket_number;
      setTransferPrefill(null);
      setTransferCompanyName(CANNIBAL_TRANSFER_DEFAULT_COMPANY);
      setRefresh((value) => value + 1);
      toast.success(ticketNumber ? `Service ticket ${ticketNumber} created successfully.` : "Service ticket created successfully.");
    } catch (reason) {
      setTransferError(formatApiError(reason));
    } finally {
      setTransferSubmitting(false);
    }
  };
  return (
    <OrdinaryPageBody data-testid="cannibal-devices-page">
      <OrdinaryPageHeader
        title="Cannibal Devices"
        actions={<>
          <RefreshButton
            onClick={() => setRefresh((value) => value + 1)}
            loading={loading}
            accessibleLabel="Refresh Cannibal Devices"
          />
          <Button
            variant="export"
            onClick={runExport}
            loading={exporting}
            loadingLabel="Exporting..."
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
          <Button onClick={() => setOverlay({ type: "create" })}>
            <Plus className="h-3.5 w-3.5" />
            Add Device
          </Button>
        </>}
        data-testid="cannibal-page-header"
      />
      <ListTableShell>
        <ServiceBranchSearchRow
          selectedBranch={filters.branch}
          onBranchChange={(branch) => applyFilter("branch", branch)}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          testIdPrefix="cannibal"
          beforeSearch={(<>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="neutral"
                  className="h-9 min-w-[168px] shrink-0 justify-between rounded-sm border-input bg-card px-3 text-foreground shadow-none hover:border-input hover:bg-card hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-offset-0"
                  aria-label={`Filter Cannibal donors by status: ${filters.status === "active" ? "Active" : "Transferred"}`}
                  data-testid="cannibal-status-filter"
                >
                  <Filter aria-hidden="true" />
                  <span className="flex-1 text-left">
                    {filters.status === "active" ? "Active" : "Transferred"}
                  </span>
                  <ChevronDown className="text-zinc-400" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[168px]"
                data-testid="cannibal-status-menu"
              >
                {[
                  ["active", "Active"],
                  ["transferred", "Transferred"],
                ].map(([value, label]) => {
                  const selected = filters.status === value;
                  return (
                    <DropdownMenuItem
                      key={value}
                      role="menuitemradio"
                      aria-checked={selected}
                      onSelect={() => {
                        if (!selected) applyFilter("status", value);
                      }}
                      className="justify-between rounded-[var(--action-radius-compact)]"
                      data-testid={`cannibal-status-${value}`}
                    >
                      <span>{label}</span>
                      {selected ? (
                        <Check
                          className="text-[var(--neon-cyan-outline-foreground)]"
                          aria-hidden="true"
                        />
                      ) : null}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="w-[170px] shrink-0" data-testid="cannibal-created-date-range">
              <DateRangeSelector
                value={createdDateRange}
                onChange={setCreatedDateRange}
                onApply={applyCreatedDateRange}
                mode="immediate"
                label={null}
                aria-label="Cannibal Created Date Range"
                controlsClassName="h-9"
                id="cannibal-created-date-range"
              />
            </div>
          </>)}
          searchControl={(
            <GlobalSearch
              key={`cannibal-global-search-${filterResetKey}`}
              value={searchInput}
              onValueChange={setSearchInput}
              onApply={(value) => applyFilter("search", value)}
              placeholder="Global Search"
              inputProps={{
                "aria-label": "Global Search",
                "data-testid": "cannibal-search-input",
              }}
            />
          )}
        />
        {error ? (
          <ListErrorNotice>{error}</ListErrorNotice>
        ) : null}
        <ListTableViewport>
          <ListTable geometry={CANNIBAL_TABLE_GEOMETRY} data-testid="cannibal-table">
            <thead>
              <tr>
                <th>Cannibal ID</th>
                <th>Serial Number</th>
                <th>Model</th>
                <th>Stock Reference</th>
                <th>Removed Parts</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
              <ColumnFilterRow key={`cannibal-column-filters-${filterResetKey}`} data-testid="cannibal-filter-row">
                <ColumnFilterCell>
                  <ColumnTextFilter
                    value={columnFilterDrafts.cannibal_id}
                    onValueChange={(value) => updateColumnFilterDraft("cannibal_id", value)}
                    onApply={(value) => applyFilter("cannibal_id", value)}
                    placeholder="Filter..."
                    aria-label="Filter Cannibal ID"
                    data-testid="cannibal-filter-cannibal-id"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnTextFilter
                    value={columnFilterDrafts.serial_number}
                    onValueChange={(value) => updateColumnFilterDraft("serial_number", value)}
                    onApply={(value) => applyFilter("serial_number", value)}
                    placeholder="Filter..."
                    aria-label="Filter Serial Number"
                    data-testid="cannibal-filter-serial-number"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnTextFilter
                    value={columnFilterDrafts.model}
                    onValueChange={(value) => updateColumnFilterDraft("model", value)}
                    onApply={(value) => applyFilter("model", value)}
                    placeholder="Filter..."
                    aria-label="Filter Model"
                    data-testid="cannibal-filter-model"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnSelectFilter
                    value={columnFilterDrafts.stock_reference_status}
                    onValueChange={(value) => updateColumnFilterDraft("stock_reference_status", value)}
                    onApply={(value) => applyFilter("stock_reference_status", value)}
                    options={CANNIBAL_STOCK_REFERENCE_OPTIONS}
                    placeholder="All"
                    aria-label="Filter Stock Reference"
                    data-testid="cannibal-filter-stock-reference"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnTextFilter
                    value={columnFilterDrafts.removed_parts}
                    onValueChange={(value) => updateColumnFilterDraft("removed_parts", value)}
                    onApply={(value) => applyFilter("removed_parts", value)}
                    placeholder="Filter..."
                    aria-label="Filter Removed Parts"
                    data-testid="cannibal-filter-removed-parts"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnDateFilter
                    value={columnFilterDrafts.created_date}
                    onValueChange={(value) => updateColumnFilterDraft("created_date", value)}
                    onApply={(value) => applyFilter("created_date", value)}
                    aria-label="Filter Created date"
                    data-testid="cannibal-filter-created-date"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnDateFilter
                    value={columnFilterDrafts.updated_date}
                    onValueChange={(value) => updateColumnFilterDraft("updated_date", value)}
                    onApply={(value) => applyFilter("updated_date", value)}
                    aria-label="Filter Updated date"
                    data-testid="cannibal-filter-updated-date"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell className="align-middle" aria-label="Actions filter controls">
                  <div className="flex h-7 w-full items-center justify-end">
                    <ClearAllFilters
                      onClick={clearAllFilters}
                      disabled={
                        !filters.search
                        && !filters.created_date_from
                        && !filters.created_date_to
                        && !CANNIBAL_COLUMN_FILTER_KEYS.some((key) => filters[key])
                      }
                      data-testid="cannibal-filter-clear-all"
                    />
                  </div>
                </ColumnFilterCell>
              </ColumnFilterRow>
            </thead>
            <tbody>
              {loading ? (
                <ListTableState colSpan={8} kind="loading">Loading Cannibal donors...</ListTableState>
              ) : donors.length === 0 ? (
                <ListTableState colSpan={8}>No Cannibal donors match.</ListTableState>
              ) : (
                donors.map((donor) => {
                  const canEdit = donor.status !== "transferred";
                  const canTransfer = donor.status === "active";
                  return (
                  <tr key={donor.id}>
                    <td className="max-w-[240px] text-xs font-semibold"><DataValue value={donor.cannibal_id} containment="break-all" /></td>
                    <td className="max-w-[180px] text-xs font-semibold"><DataValue value={donor.serial_number} containment="truncate" /></td>
                    <td className="max-w-[180px] text-xs"><DataValue value={donor.model} /></td>
                    <td className="text-xs capitalize">
                      {donor.stock_reference_status}
                    </td>
                    <td className="num !text-center">{donor.removed_parts?.length || 0}</td>
                    <td className="text-xs">{formatDate(donor.created_at)}</td>
                    <td className="text-xs">{formatDate(donor.updated_at)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="info"
                          size="row-icon"
                          title="View"
                          aria-label={`View Cannibal donor ${donor.cannibal_id || donor.serial_number}`}
                          onClick={() =>
                            setOverlay({ type: "view", id: donor.id })
                          }
                        >
                          <Eye className="h-4 w-4" strokeWidth={2} />
                        </Button>
                        {canEdit || canTransfer ? <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="neutral"
                              size="row-icon"
                              title="More actions"
                              aria-label={`More actions for Cannibal donor ${donor.cannibal_id || donor.serial_number}`}
                            >
                              <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {canEdit ? (
                              <DropdownMenuItem
                                onSelect={() =>
                                  setOverlay({ type: "edit", id: donor.id })
                                }
                              >
                                <Edit3 className="h-3.5 w-3.5" strokeWidth={2} />
                                Edit
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem
                              onSelect={() => setOverlay({ type: "history", donor })}
                            >
                              <History className="h-3.5 w-3.5" strokeWidth={2} />
                              History
                            </DropdownMenuItem>
                            {canTransfer ? (
                              <DropdownMenuItem
                                onSelect={() => openTransfer(donor.id)}
                                disabled={Boolean(transferLoadingId)}
                              >
                                <Send className="h-3.5 w-3.5" strokeWidth={2} />
                                {transferLoadingId === donor.id ? "Loading Transfer..." : "Transfer to Service Stock"}
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu> : (
                          <Button
                            type="button"
                            variant="info"
                            size="row-icon"
                            title="History"
                            aria-label={`View history for Cannibal donor ${donor.cannibal_id || donor.serial_number}`}
                            onClick={() => setOverlay({ type: "history", donor })}
                          >
                            <History className="h-4 w-4" strokeWidth={2} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </ListTable>
        </ListTableViewport>
        <ListPagination
          page={pagination.page || page}
          pageSize={pageSize}
          total={pagination.total}
          totalPages={pagination.total_pages}
          loading={loading}
          onPrevious={() => setPage((value) => Math.max(1, value - 1))}
          onNext={() => setPage((value) => value + 1)}
          onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); }}
          pageSizeAriaLabel="Cannibal donors rows per page"
          summaryTestId="cannibal-pagination-summary"
        />
      </ListTableShell>
      {overlay?.type === "history" ? (
        <CannibalHistoryDrawer
          donor={overlay.donor}
          onClose={() => setOverlay(null)}
        />
      ) : overlay ? (
        <DonorOverlay
          mode={overlay.type}
          donorId={overlay.id}
          role={user?.role}
          onClose={() => setOverlay(null)}
          onChanged={() => setRefresh((value) => value + 1)}
        />
      ) : null}
      <CannibalTransferDialog
        prefill={transferPrefill}
        companyName={transferCompanyName}
        onCompanyNameChange={(value) => {
          setTransferCompanyName(value);
          setTransferError("");
        }}
        onCancel={closeTransfer}
        onTransfer={submitTransfer}
        submitting={transferSubmitting}
        error={transferError}
      />
    </OrdinaryPageBody>
  );
}
